import React, { useState, useEffect, useCallback } from 'react';
import { listarAsientos, obtenerGrupoAsiento, editarGrupoAsiento, obtenerCuentas } from '../services/asientosService';
import { showError, showSuccess } from '../utils/alerts';
import AsientoLineasEditor, { entriesToPayload, sumaMontos } from '../components/common/AsientoLineasEditor';
import '../styles/pages/AsientosContables.css';

const MESES = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' },
];

const anioActual = new Date().getFullYear();
const LIMIT = 20;

const formatMonto = (n) => (n === null || n === undefined ? '' : Number(n).toLocaleString('es-CL'));

// Se extraen los componentes año-mes-día por texto (sin pasar por Date/timezone)
// para evitar desfases de huso horario, y se muestran en formato chileno DD-MM-AAAA.
const formatFechaCL = (fecha) => {
    if (!fecha) return '';
    const [y, m, d] = String(fecha).slice(0, 10).split('-');
    if (!y || !m || !d) return String(fecha);
    return `${d}-${m}-${y}`;
};

const AsientosContables = ({ id_company }) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ anio: String(anioActual), mes: '', cuenta: '', origen: '' });
    const [applied, setApplied] = useState({ anio: String(anioActual), mes: '', cuenta: '', origen: '' });
    const [editando, setEditando] = useState(null);

    const fetchRows = useCallback(async (p, f) => {
        setLoading(true);
        try {
            const data = await listarAsientos({ id_company, page: p, limit: LIMIT, ...f });
            setRows(data.rows);
            setTotal(data.total);
        } catch (error) {
            showError('Error cargando los asientos contables');
        } finally {
            setLoading(false);
        }
    }, [id_company]);

    useEffect(() => {
        fetchRows(1, applied);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id_company]);

    const handleApply = () => {
        setPage(1);
        setApplied({ ...filters });
        fetchRows(1, filters);
    };

    const handlePage = (p) => {
        setPage(p);
        fetchRows(p, applied);
    };

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));

    // Se ocultan (no se eliminan, siguen en la BD) las líneas sin valor en
    // ningún lado: no aportan información al usuario. Si cualquiera de los
    // dos lados tiene un monto > 0, la línea es relevante y se muestra.
    const esCero = (v) => v === null || v === undefined || Number(v) === 0;
    const rowsVisibles = rows.filter((r) => !(esCero(r.debe) && esCero(r.haber)));

    return (
        <div className="asientos-contables-page">
            <h1>Asientos Contables</h1>

            <div className="filters-bar">
                <input
                    type="number"
                    placeholder="Año"
                    value={filters.anio}
                    onChange={(e) => setFilters((f) => ({ ...f, anio: e.target.value }))}
                />
                <select value={filters.mes} onChange={(e) => setFilters((f) => ({ ...f, mes: e.target.value }))}>
                    <option value="">Todos los meses</option>
                    {MESES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <input
                    placeholder="Cuenta"
                    value={filters.cuenta}
                    onChange={(e) => setFilters((f) => ({ ...f, cuenta: e.target.value }))}
                />
                <select value={filters.origen} onChange={(e) => setFilters((f) => ({ ...f, origen: e.target.value }))}>
                    <option value="">Todos los orígenes</option>
                    <option value="manual">Manual</option>
                    <option value="automatico">Sistema</option>
                </select>
                <button className="btn-primary btn-small" onClick={handleApply}>Filtrar</button>
            </div>

            {loading ? (
                <div className="loading">Cargando...</div>
            ) : (
                <>
                    <div className="log-count">{total.toLocaleString()} líneas</div>
                    <table className="asientos-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Cuenta</th>
                                <th>Debe</th>
                                <th>Haber</th>
                                <th>Descripción</th>
                                <th>Origen</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rowsVisibles.length === 0 ? (
                                <tr><td colSpan={7} className="empty">Sin registros para este período</td></tr>
                            ) : (
                                rowsVisibles.map((r) => (
                                    <tr key={r.id}>
                                        <td>{formatFechaCL(r.fecha)}</td>
                                        <td>{r.cuenta}</td>
                                        <td>{formatMonto(r.debe)}</td>
                                        <td>{formatMonto(r.haber)}</td>
                                        <td>{r.descripcion}</td>
                                        <td>
                                            <span className={`badge ${r.es_manual ? 'badge-manual' : 'badge-sistema'}`}>
                                                {r.es_manual ? 'Manual' : 'Sistema'}
                                            </span>
                                        </td>
                                        <td>
                                            {r.es_manual && r.id_asiento_grupo ? (
                                                <button className="btn-secondary btn-small" onClick={() => setEditando(r.id_asiento_grupo)}>
                                                    Editar
                                                </button>
                                            ) : (
                                                !r.es_manual && <span className="asiento-solo-informativo" title="Generado automáticamente por el sistema — no editable">Solo informativo</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button disabled={page === 1} onClick={() => handlePage(page - 1)}>← Anterior</button>
                            <span>Página {page} de {totalPages}</span>
                            <button disabled={page === totalPages} onClick={() => handlePage(page + 1)}>Siguiente →</button>
                        </div>
                    )}
                </>
            )}

            {editando && (
                <EditarAsientoModal
                    idAsientoGrupo={editando}
                    onClose={() => setEditando(null)}
                    onSaved={() => { setEditando(null); fetchRows(page, applied); }}
                />
            )}
        </div>
    );
};

function EditarAsientoModal({ idAsientoGrupo, onClose, onSaved }) {
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [cuentas, setCuentas] = useState([]);
    const [fecha, setFecha] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [tipo, setTipo] = useState(null);
    const [categoria, setCategoria] = useState(null);
    const [debeEntries, setDebeEntries] = useState([]);
    const [haberEntries, setHaberEntries] = useState([]);

    useEffect(() => {
        const cargar = async () => {
            try {
                const [filas, listaCuentas] = await Promise.all([
                    obtenerGrupoAsiento(idAsientoGrupo),
                    obtenerCuentas(),
                ]);
                setCuentas(listaCuentas);
                if (filas.length > 0) {
                    setFecha((filas[0].fecha || '').slice(0, 10));
                    setDescripcion(filas[0].descripcion || '');
                    setTipo(filas[0].tipo ?? null);
                    setCategoria(filas[0].categoria ?? null);
                }
                setDebeEntries(
                    filas.filter((f) => f.debe !== null).map((f) => ({ cuenta: f.cuenta, monto: Number(f.debe).toLocaleString('es-CL') }))
                );
                setHaberEntries(
                    filas.filter((f) => f.haber !== null).map((f) => ({ cuenta: f.cuenta, monto: Number(f.haber).toLocaleString('es-CL') }))
                );
            } catch (error) {
                showError(error.response?.data || 'Error cargando el asiento contable');
                onClose();
            } finally {
                setCargando(false);
            }
        };
        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idAsientoGrupo]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (sumaMontos(debeEntries) !== sumaMontos(haberEntries)) {
            showError('El asiento no está balanceado: la suma del Debe debe ser igual a la suma del Haber');
            return;
        }

        setGuardando(true);
        try {
            await editarGrupoAsiento(idAsientoGrupo, {
                fecha,
                descripcion,
                tipo,
                categoria,
                debeEntries: entriesToPayload(debeEntries),
                haberEntries: entriesToPayload(haberEntries),
            });
            showSuccess('Asiento contable actualizado exitosamente');
            onSaved();
        } catch (error) {
            showError(error.response?.data || 'Error al editar el asiento contable');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                <h3>Editar asiento contable</h3>
                {cargando ? (
                    <div className="loading">Cargando...</div>
                ) : (
                    <form onSubmit={handleSubmit} className="editar-asiento-form">
                        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
                        <AsientoLineasEditor
                            cuentas={cuentas}
                            debeEntries={debeEntries}
                            haberEntries={haberEntries}
                            onDebeEntriesChange={setDebeEntries}
                            onHaberEntriesChange={setHaberEntries}
                        />
                        <input type="text" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Glosa" required />
                        <div className="modal-actions">
                            <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
                            <button type="submit" className="btn-primary" disabled={guardando}>
                                {guardando ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default AsientosContables;
